import type { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import {
  ATTENTION_STATUS_ACTIONS,
  ATTENTION_STATUS_BITS,
  getAttentionStatusActions,
  getAttentionStatusRole,
} from 'partner360/entities/attentionStatuses'
import { getTranslatedText } from 'partner360/helpers/getTranslatedText'

import { FinancialCockpitPendingActionOwner } from '../../../../api/types'
import { getCockpitFiscalYearLabel } from '../../../../lib'
import { selectCockpitUserAccess } from '../../../../model/selectors'
import { usePendingActionInvocation } from '../../../cockpitPendingActions/hooks/usePendingActionInvocation'
import { useAttentionDetailExit } from '../../hooks/useAttentionDetailExit'
import type { IAttentionStatusesItem } from '../../types'
import {
  buildSobEntryAction,
  SOB_KEYS,
  useMissingSobCategories,
} from '../attentionStatusSobActuals'

import { AttentionNextStepRow } from './AttentionNextStepRow'
import { AttentionNextStepsGroup } from './AttentionNextStepsGroup'
import { NEXT_STEPS_LIMIT } from './constants'
import { getStepInitials } from './getStepInitials'

interface ISobNextStepsProps {
  item: IAttentionStatusesItem & { offering: string }
  onSelect: (id: string) => void
}

/**
 * `SOB Actuals are missing`: the first categories still owing their actuals, in the planning tab's
 * order, each opening the SOB dialog on its category — the same dialog, through the same pending
 * action, as the status' detail. A category the server has locked keeps its button disabled.
 */
export const SobNextSteps = ({ item, onSelect }: ISobNextStepsProps) => {
  const t = useSafetyTranslation()
  const offering = item.offering as ProgramTypes
  const { role } = useSelector(selectCockpitUserAccess)
  const { categories, fYear, fQuarter } = useMissingSobCategories(offering)
  const { selectAction, isActionBusy, modals } = usePendingActionInvocation(offering)
  const exitDetail = useAttentionDetailExit()

  const canUpdate = getAttentionStatusActions(
    ATTENTION_STATUS_BITS.sobActuals,
    getAttentionStatusRole(role)
  ).some(({ id }) => id === 'updateSob')

  const periodLabel = [getCockpitFiscalYearLabel(fYear), fQuarter === null ? '' : `Q${fQuarter}`]
    .filter(Boolean)
    .join(' ')

  const rows = useMemo(
    () =>
      categories.slice(0, NEXT_STEPS_LIMIT).map(({ category, action, quartersWithSob }, index) => ({
        category,
        quartersWithSob,
        action:
          action ??
          buildSobEntryAction(
            category,
            canUpdate
              ? FinancialCockpitPendingActionOwner.Mine
              : FinancialCockpitPendingActionOwner.PBM,
            index
          ),
      })),
    [canUpdate, categories]
  )

  return (
    <AttentionNextStepsGroup
      item={item}
      onSelect={onSelect}
      restCount={categories.length - rows.length}
    >
      {rows.length === 0 ? null : (
        <>
          {rows.map(({ category, quartersWithSob, action }) => (
            <AttentionNextStepRow
              key={category}
              mark={{
                kind: 'category',
                text: getStepInitials(getTranslatedText({ t, key: category })),
              }}
              name={<Translation span text={category} />}
              fact={
                <Translation
                  span
                  text={quartersWithSob.length ? SOB_KEYS.rowPartial : SOB_KEYS.rowMissing}
                  replaceParams={[
                    { key: '{period}', value: periodLabel },
                    {
                      key: '{quarters}',
                      value: quartersWithSob.map((quarter) => `Q${quarter}`).join(', '),
                    },
                  ]}
                />
              }
              tone="go"
              severity={item.severity}
              labelKey={ATTENTION_STATUS_ACTIONS.updateSob.labelKey}
              disabled={Boolean(action.ActionBlockedBy) || isActionBusy(action)}
              onClick={() => {
                if (selectAction(action)) {
                  exitDetail()
                }
              }}
            />
          ))}
          {modals}
        </>
      )}
    </AttentionNextStepsGroup>
  )
}

SobNextSteps.displayName = 'SobNextSteps'
